import { LocalStorageService } from 'angular-2-local-storage';
import { InteractivityActionService } from './../../services/interactivity-action.service';
import { InteractivityDesignDimensionsService } from './../../services/interactivity-design-dimensions.service';
import { IWidgetProperty } from './../../interfaces/IWidgetProperty';
import { TranslateService } from '@ngx-translate/core';

import { Component, Input, ViewChild, TemplateRef, SimpleChanges, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { bbGenerateShortId } from '../../../../../shared/utils/bbGenerateShortId.util';
import { InteractivityEditorService } from '../../services/interactivity-editor.service';
import { IWidgetGroupTag } from '../../redux/timeline-data.model';
import { InteractivityDataSourceService } from '../../services/interactivity-data-source.service';
import { InteractivityWidgetSelectionService } from '../../services/interactivity-widget-selection.service';
import { IWidgetSelection } from '../../interfaces/IWidgetSelection';
import { InteractivityWidgetService } from '../../services/interactivity-widget.service';
import { InteractivityStateService } from '../../services/interactivity-state.service';
import { Widget } from '../../classes/Widget';
import { removeFileExtension } from 'app/shared/utils/file.util';

@Component({
	selector: 'ie-widget-fields',
	templateUrl: './ie-widget-fields.component.html',
	styleUrls: ['./ie-widget-fields.component.scss'],
})
export class IEWidgetFieldsComponent implements OnInit, OnChanges, OnDestroy {
	@Input() selectedWidget: Widget;
	@Input() selectedWidgetTags: any[] = [];
	@Input() properties: any[];
	@Input() withPreview = false;
	@ViewChild('dataSourceTemplate') dataSourceTemplate: TemplateRef<any>;

	selectionGroupTags: any[] = [];
	selectionGroupTagsPartial: any[] = [];
	widgetSelection: IWidgetSelection;
	multiSelect = false;
	allWidgetTags: any = [];
	foundProp: any = {};
	tProps: any = [];
	checkboxProps: any = [];
	propertiesOfSelectedWidgets: IWidgetProperty[];
	mayRenderRef: any = {};
	states: any = [];
	stateOverrideOptions: any[] = [];
	statePaneOpen: any = {};
	visualFields: any = [];
	visualProps = [
		'textFormat/textFormat/embeddedfont',
		'textFormat/textFormat/fontsize',
		'textFormat/textFormat/color',
		'textFormat/textFormat/bold',
		'textFormat/textFormat/italic',
		'textFormat/textFormat/underline',
		'textFormat/textFormat/margin',
		'textFormat/textFormat/align',
		'textFormat/textFormat/verticalalign',
		'backgroundcolor',
		'background_alpha',
		'alpha',
		'color',
		'bordercolor',
		'borderthickness',
		'cornerradius',
		// 'imageid', // maybe later, image widget has extra props: imageid imageid_mousedown imageid_mouseover imageid_toggle
		// 'cssclass', // maybe later, check if datasource is used
	];
	webFonts: any = [
		{id: '-2', originalfilename: 'Arial', fallback: 'Helvetica, sans-serif' },
		{id: '-3', originalfilename: 'Arial Black', fallback: 'Gadget, sans-serif' },
		{id: '-4', originalfilename: 'Bookman Old Style', fallback: 'serif' },
		{id: '-5', originalfilename: 'Comic Sans MS', fallback: 'cursive, sans-serif' },
		{id: '-6', originalfilename: 'Courier', fallback: 'monospace' },
		{id: '-7', originalfilename: 'Courier New', fallback: 'Courier, monospace' },
		{id: '-8', originalfilename: 'Garamond', fallback: 'serif' },
		{id: '-9', originalfilename: 'Georgia', fallback: 'serif' },
		{id: '-10', originalfilename: 'Lucida Console', fallback: 'Monaco, monospace' },
		{id: '-11', originalfilename: 'Lucida Sans Unicode', fallback: 'Lucida Grande, sans-serif' },
		{id: '-12', originalfilename: 'MS Sans Serif', fallback: 'Geneva, sans-serif' },
		{id: '-13', originalfilename: 'MS Serif', fallback: 'New York, sans-serif' },
		{id: '-14', originalfilename: 'Palatino Linotype', fallback: 'Book Antiqua, Palatino, serif' },
		{id: '-15', originalfilename: 'Symbol', fallback: 'sans-serif' },
		{id: '-16', originalfilename: 'Tahoma', fallback: 'Geneva, sans-serif' },
		{id: '-17', originalfilename: 'Times New Roman', fallback: 'Times, serif' },
		{id: '-18', originalfilename: 'Trebuchet MS', fallback: 'Helvetica, sans-serif' },
		{id: '-1', originalfilename: 'Verdana', fallback: 'Geneva, sans-serif' } // default
	].map((o: any) => {
		o.isWebFont = true;
		o.mediatype = 'font';
		o.label = o.originalfilename;
		o.title = o.originalfilename;
		return o;
	});

	private subscriptions = new Subscription();

	constructor(
		public interactivityEditorService: InteractivityEditorService,
		public interactivityDesignDimensionsService: InteractivityDesignDimensionsService,
		private widgetSelectionService: InteractivityWidgetSelectionService,
		private interactivityActionService: InteractivityActionService,
		private localStorageService: LocalStorageService,
		public interactivityWidgetService: InteractivityWidgetService,
		private stateService: InteractivityStateService,
		private translate: TranslateService,
		private dataSourceService: InteractivityDataSourceService
	) {}

	ngOnInit() {
		this.collapsed = true;

		if (this.selectedWidget && this.selectedWidget.config && !this.selectedWidget.config.validation) {
			// our selected widget does not have a complex validation object yet, so adding this now
			this.selectedWidget.config.validation = {
				validation: {
					externalValidation: '',
					validationType: '',
					validationMethod: '',
				},
			};
		}

		this.subscriptions.add(
			this.interactivityEditorService.groupTagSubject.subscribe((groupTags: IWidgetGroupTag[]) => {
				if (groupTags) {
					this.allWidgetTags = groupTags.map((groupTag) => ({
						label: groupTag.label,
						value: groupTag.id,
					}));
				}
			})
		);

		this.subscriptions.add(
			this.widgetSelectionService.widgetSelectionSubject.subscribe((data: IWidgetSelection) => {
				this.widgetSelection = data;
				this.selectionGroupTags = [];
				this.selectionGroupTagsPartial = [];
				const groupTagCounts: any = {};
				this.propertiesOfSelectedWidgets = data.commonProperties;
				this.tProps = [];
				this.checkboxProps = [];
				if (!data?.widgets?.length) {
					this.visualFields = [];
					this.multiSelect = false;
					return;
				}

				if (this.selectedWidget?.interfaceConfig?.default?.['textFormat/textFormat/font'] && !this.selectedWidget?.interfaceConfig?.default?.['textFormat/textFormat/embeddedfont']) {
					this.fillEmbeddedFont(this.selectedWidget?.interfaceConfig.default);
				}
				this.visualFields = this.propertiesOfSelectedWidgets
					.filter((field) => this.visualProps.includes(field.key))
					.map((prop) => ({
						label: this.translate.instant(
							'WIDGET_PROP_' + prop.key.toUpperCase().replace(/TEXTFORMAT\/TEXTFORMAT\//g, '')
						),
						key: prop.key,
						value: prop.key,
						shortKey: this.getShortKey(prop.key),
						config: this.interactivityWidgetService.getComponentConfigFromProperty(prop),
						fieldConfig: (()=> {
							const itype = this.interactivityActionService.getPropertyValueType(prop.key, true);
							return {
								type: 'string',
								itype,
								extraOptions: this.webFonts,
								icon: itype === 'togglebutton' ? prop.key?.split('/').at(-1) : ''
							};
						})()
					}));
				this.stateOverrideOptions = this.visualFields.filter((option) => ![
					'textFormat/textFormat/margin',
					'textFormat/textFormat/font',
					'textFormat/textFormat/embeddedfont'
				].concat(
					Object.keys(this.selectedWidget?.interfaceConfig?.mouseover || {})
				).includes(option.key));
				this.propertiesOfSelectedWidgets.forEach((prop) => {
					if ('xpos ypos width height'.indexOf(prop.key) !== -1) {
						this.tProps.push(this.interactivityWidgetService.getComponentConfigFromProperty(prop));
					}
					if ('preload loop useAsKPI showControls showOnMouseHover'.indexOf(prop.key) !== -1) {
						this.checkboxProps.push(this.interactivityWidgetService.getComponentConfigFromProperty(prop));
					}
				});

				if (data.widgets.length > 1) {
					this.multiSelect = true;
					// multiple widgets selected
					data.widgets.forEach((widget) => {
						if (widget && widget.grouping && widget.grouping.length) {
							widget.grouping.forEach((widgetGroupTag) => {
								if (this.selectionGroupTags.indexOf(widgetGroupTag) === -1) {
									this.selectionGroupTags.push(widgetGroupTag);
								}
								groupTagCounts[widgetGroupTag] = groupTagCounts[widgetGroupTag]
									? groupTagCounts[widgetGroupTag] + 1
									: 1;
							});
						}
					});
					this.selectionGroupTags.forEach((tag) => {
						if (
							groupTagCounts[tag] !== data.widgets.length &&
							this.selectionGroupTagsPartial.indexOf(tag) === -1
						) {
							this.selectionGroupTagsPartial.push(tag);
						}
					});
				}
			})
		);
	}

	fillEmbeddedFont(fieldConfig: any) {
		setTimeout(() => {
			fieldConfig['textFormat/textFormat/embeddedfont'] = this.webFonts.find((font) => font.originalfilename === fieldConfig['textFormat/textFormat/font'])?.id;
		});
	}

	shouldStateOpen(stateName: string) {
		return this.localStorageService.get('ovp-ie-state-open-' + stateName);
	}

	onStateToggled(stateName: string, open) {
		this.localStorageService.set('ovp-ie-state-open-' + stateName, open);
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.properties && simpleChanges.properties.currentValue) {
			this.mayRenderRef = {};
			this.properties.forEach((item) => {
				this.mayRenderRef[item.key] = true;
			});
		}
		if (simpleChanges?.selectedWidget?.currentValue) {
			this.onSelectedWidgetChange();
		}
	}

	getShortKey(key: string) {
		return key.replace('textFormat/textFormat/', 'tf-');
	}

	onSelectedWidgetChange() {
		if (this.selectedWidget?.isNew) {
			this.collapsed = false;
		}
		this.updateStates();
		if (this.selectedWidget?.interfaceConfig?.default?.['textFormat/textFormat/font'] && !this.selectedWidget?.interfaceConfig?.default?.['textFormat/textFormat/embeddedfont']) {
			this.fillEmbeddedFont(this.selectedWidget?.interfaceConfig.default);
		}
	}

	updateStates() {
		if (!this.selectedWidget) {
			return;
		}

		const usedStates = ['default'];
		if (['button'].includes(this.selectedWidget.type)) {
			usedStates.push('mouseover');
		}

		this.states = usedStates.map((key) => {
			this.statePaneOpen[key] = this.shouldStateOpen(key);
			return {
				name: key,
				config: this.selectedWidget.getConfigByState('default', key),
			};
		});
	}

	set collapsed(val: any) {
		const state = this.stateService.getPropState('misc');
		state.sidebarHeaderCollapsed = val;
		this.stateService.setPropState('misc', state);
	}

	get collapsed() {
		const state = this.stateService.getPropState('misc') || {};
		return state.sidebarHeaderCollapsed;
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	public mayRender(propertyName: string, ignoreCollapsed = false): boolean {
		if (this.collapsed && !ignoreCollapsed) {
			return false;
		}
		return !!this.mayRenderRef[propertyName];
	}

	onDataSourceClicked(key: string) {
		if (this.properties) {
			this.foundProp = this.properties.find((prop) => prop && prop.key === key);
			this.dataSourceService.onDataSourceClicked(this.foundProp, this.dataSourceTemplate);
		}
	}

	onWidgetTagsChanged(value: any, fromMultiSelect = false) {
		let newTag = null;
		if (value && value.length) {
			const newValue = value[value.length - 1];
			const tagExists = this.allWidgetTags.find((item: any) => newValue === item.value);
			if (!tagExists) {
				newTag = {
					id: bbGenerateShortId(),
					label: newValue,
				};
				value[value.length - 1] = newTag.id;
			}
			this.interactivityEditorService.changeWidgetTags(
				this.selectedWidget,
				this.selectedWidgetTags,
				null,
				newTag
			);
		} else {
			this.interactivityEditorService.changeWidgetTags(this.selectedWidget, []);
		}
	}

	onWidgetsTagAddedRemoved(option: any, removed = false) {
		let newTag = null;
		let existingTag = this.allWidgetTags.find((item: any) => option.value === item.value);
		if (!removed && !existingTag) {
			newTag = {
				id: bbGenerateShortId(),
				label: option.label,
			};
		} else if (existingTag) {
			existingTag = { label: existingTag.label, id: existingTag.value };
		}

		this.interactivityEditorService.addRemoveTagsOnWidget(
			this.widgetSelection.widgets,
			newTag || existingTag,
			newTag,
			removed
		);
	}

	onDataSourceChanged(): void {
		this.changeProperty(
			this.foundProp.key,
			this.foundProp.value,
			`change widget property: ${this.foundProp.key}` + typeof this.foundProp.value === 'string'
				? ' to ' + this.foundProp.value
				: ''
		);
		this.foundProp = {};
	}

	trackBy(i: number, prop: any) {
		return (prop && prop.key) || i;
	}

	stateTrackBy(i: number, state: any) {
		return (state && state.name) || i;
	}

	onStateFormFieldOptionSelected(fieldName: string, selectedOption: any, state = null) {
		if (fieldName.endsWith('embeddedfont')) {
			const isWebFont = selectedOption?.id?.startsWith?.('-');
			this.onFormFieldChanged(fieldName, false, this.interactivityDesignDimensionsService.translateValueForRedux({key: fieldName, val: selectedOption?.id}), state);
			fieldName = 'textFormat/textFormat/font';
			this.onFormFieldChanged(fieldName, false, this.interactivityDesignDimensionsService.translateValueForRedux({key: fieldName, val:
				isWebFont ? selectedOption.originalfilename : removeFileExtension(selectedOption.originalfilename)
			}), state);
		}

	}

	onStateFormFieldChanged(fieldName: string, useSelection = false, value: any, state = null, remove = false) {
		if (fieldName.endsWith('font') && value?.startsWith?.('-')) {
			// handled by onStateFormFieldOptionSelected
			return;
		}
		this.onFormFieldChanged(fieldName, useSelection, this.interactivityDesignDimensionsService.translateValueForRedux({key: fieldName, val: value}), state, remove);
	}

	removeStateProperty(propertyOption, state) {
		this.onStateFormFieldChanged(propertyOption.value, false, null, state, true);
	}

	onAddStateOptionSelected(propertyOption, state) {
		this.interactivityWidgetService.addStateProperty(propertyOption?.value, state);
	}

	onFormFieldChanged(fieldName: string, useSelection = false, value: any, state = null, remove = false) {
		const val = remove
			? undefined
			: value || (this.selectedWidget && this.selectedWidget.config && this.selectedWidget.config[fieldName]);
		if (state) {
			this.interactivityEditorService.setTmpState(state);
		}
		if (useSelection) {
			this.interactivityWidgetService.changeProperty(
				{
					key: fieldName,
					val,
				},
				null,
				remove
					? `removed widget property: ${fieldName} from the ${this.interactivityEditorService.activeState} state`
					: `change widget property: ${fieldName}` +
						typeof val === 'string'	?
						' to ' + val
						: '',
				remove
			);
		} else {
			this.changeProperty(
				fieldName,
				val,
				`change widget property: ${fieldName}` + typeof val === 'string' ? ' to ' + val : ''
			);
		}
	}

	multiWidgetChange(key, partial) {
		if (this.widgetSelection && this.widgetSelection.widgets && this.widgetSelection.widgets.length) {
			for (let i = 0; i < this.widgetSelection.widgets.length; i++) {
				if (this.widgetSelection.widgets[i].config[key] === 'false' || partial) {
					this.interactivityEditorService.changeWidgetsProperty(
						[this.widgetSelection.widgets[i]],
						key,
						'true'
					);
				} else {
					this.interactivityEditorService.changeWidgetsProperty(
						[this.widgetSelection.widgets[i]],
						key,
						'false'
					);
				}
			}
		}
	}

	onValidationChanged(fieldName: string, value: any) {
		const validationObject = this.selectedWidget.config.validation;
		validationObject.validation[fieldName] = value;

		if (value === 'BBStandard') {
			validationObject.validation.externalValidation = ''; // BBStandard is selected, so clear externalValidation (custom JS)

			if (!validationObject.validation.validationType) {
				validationObject.validation.validationType = 'mandatory';
			}
		} else if (value === 'customJS') {
			validationObject.validation.validationType = ''; // CustomJS is selected, so clear validation type for BBStandard
		}

		this.changeProperty('validation', validationObject, 'change widget property: validation');
	}

	changeProperty(key: string, val: any, msg: string): void {
		this.interactivityEditorService.changeWidgetsProperty([this.selectedWidget], key, val, msg);
	}
}
